/**
 * Template Section Header
 *
 * Header component for template sections with title, optional number badge,
 * scope icon, and collapse/expand functionality.
 */

import React from 'react';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { SectionNumberBadge } from './primitives';
import type { TemplateSectionHeaderProps } from './types';

export const TemplateSectionHeader: React.FC<TemplateSectionHeaderProps> = ({
  title,
  index,
  scopeIcon,
  isSelected = false,
  collapsible = false,
  isExpanded = false,
  onToggleExpand,
  actions,
  onHeaderClick,
}) => {
  const theme = useTheme();

  const handleHeaderClick = (e: React.MouseEvent) => {
    if (collapsible && onToggleExpand) {
      e.stopPropagation();
      onToggleExpand();
    }
    onHeaderClick?.(e);
  };

  return (
    <Box
      data-section-header
      onClick={handleHeaderClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pb: collapsible ? 1.5 : 0,
        ...(collapsible && {
          borderBottom: `1px solid #E6E1FF`,
        }),
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        flex: 0,
        overflow: 'hidden',
        cursor: collapsible ? 'pointer' : 'default',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minWidth: 0,
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {/* Section Number Badge */}
        {index !== undefined && (
          <SectionNumberBadge number={index + 1} isSelected={isSelected} />
        )}

        {/* Scope Icon (for library cards) */}
        {scopeIcon && <Box sx={{ flexShrink: 0 }}>{scopeIcon}</Box>}

        {/* Section Title */}
        <Typography
          variant={index !== undefined ? 'h6' : 'subtitle2'}
          component="h3"
          sx={{
            fontSize: index !== undefined
              ? { xs: '18px', sm: '20px', md: '24px' }
              : undefined,
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Right side: Collapse button and/or actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        {/* Collapse/Expand Button */}
        {collapsible && (
          <IconButton
            size="small"
            onClick={handleHeaderClick}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}

        {/* Actions slot */}
        {actions}
      </Box>
    </Box>
  );
};

